import {
  useResponseRedirect,
  useDealershipData,
  useRedirectToPage,
} from "src/hooks";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import {
  fromBorrowerRegister,
  toAcceptCoBorrowerInviteReq,
  toBorrowerUpdateReq,
  toCreateApplicationReq,
  type RegisterFormData,
} from "src/api/adapters";
import FormLayout from "src/layouts/FormLayout";
import { Col } from "src/components/layout";
import Disclaimer from "./RegistrationDisclaimer";
import {
  notEmpty,
  validName,
  validEmail,
  validate,
  test,
  type ValidationMapperFunc,
  lessThanMaxLength,
  isValidPhoneNumber,
  addCountryCode,
  stripCountryCode,
  dealerStorage,
  getSignUpFieldLocks,
} from "src/utils";
import { useContext, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { UserContext, useAuth } from "src/context";
import {
  FormTable,
  FormTableBody,
  PersonalInfoFields,
  UserAccountIcon,
} from "@porchpass/ui";
import {
  fromPersonalInfoFormData,
  toPersonalInfoFormData,
} from "src/forms/adapters/personalInfo";
import { fieldErrorsAfterSubmit } from "src/forms/fieldErrorsAfterSubmit";
import DuplicateCoborrowerModal from "src/components/DuplicateCoborrowerModal";
import type { DtoBorrower } from "@porchpass/api-client";

const hasRegisterUrl = () => window.location.pathname.includes("/register");
const hasCoborrowerUrl = () => window.location.pathname.includes("/coborrower");
const hasPersonalInfoUrl = () =>
  window.location.pathname.includes("/personal-info");

const validationMapper: ValidationMapperFunc<RegisterFormData> = (
  formData,
) => ({
  first_name: [
    test(notEmpty, "Must not be empty"),
    test(validName, "Only letters, dashes, and spaces allowed"),
    test(lessThanMaxLength(50), "Must be less than 50 characters"),
  ],
  middle_name: [
    test(validName, "Only letters, dashes, and spaces allowed", false),
    test(lessThanMaxLength(50), "Must be less than 50 characters"),
  ],
  last_name: [
    test(notEmpty, "Must not be empty"),
    test(validName, "Only letters, dashes, and spaces allowed"),
    test(lessThanMaxLength(100), "Must be less than 100 characters"),
  ],
  suffix: [test(lessThanMaxLength(10), "Must be less than 10 characters")],
  email: [
    test(notEmpty, "Must not be empty"),
    test(validEmail, "Must be a valid email"),
    test(lessThanMaxLength(254), "Must be less than 254 characters"),
  ],
  confirm_email: hasRegisterUrl()
    ? [
        test(notEmpty, "Must not be empty"),
        test(
          () => Boolean(formData.email === formData.confirm_email),
          "Email confirmation must match email",
        ),
      ]
    : [],
  phone_number: [
    test(
      (value) => isValidPhoneNumber(stripCountryCode(value || "")),
      "Phone number should be 10 digits",
    ),
  ],
  sms_support_opt_out: hasRegisterUrl()
    ? [
        test(
          (value) => value === false,
          "You must agree to receive text messages to continue",
        ),
      ]
    : [],
});

export default () => {
  const api = useWebClientApi();
  const { user: firebaseUser } = useAuth();
  const { userData, setUserData } = useContext(UserContext);
  const { application_id, borrower_id } = useParams();
  const dealerData = useDealershipData();
  const { formData, updateField } = useFormLoad<RegisterFormData>(
    { sms_support_opt_out: hasRegisterUrl() ? true : undefined },
    () => api.getBorrower(),
    (data) => fromBorrowerRegister(data as DtoBorrower),
    { enabled: !!firebaseUser },
  );
  const redirectToRegister = useRedirectToPage("../../../../register");
  const redirectToPersonalInfo = useRedirectToPage("/personal-info/");

  // Contact info as loaded from the API. Captured once, so editing a field
  // never changes whether it is locked.
  const savedContact = useRef<{ email?: string; phone?: string } | null>(null);
  if (formData.hasPreviousData && savedContact.current === null) {
    savedContact.current = {
      email: formData.email,
      phone: formData.phone_number,
    };
  }
  const isEditingSavedBorrower = !hasRegisterUrl() && !hasCoborrowerUrl();

  const storedEmail = dealerStorage.getItem("email-for-register");
  const storedPhone = dealerStorage.getItem("phone-for-register");
  const signUpFieldLocks = getSignUpFieldLocks({
    authEmail: firebaseUser?.email,
    authPhone: firebaseUser?.phoneNumber,
    storedEmail,
    storedPhone,
    isRegisterUrl: hasRegisterUrl(),
    savedEmail: isEditingSavedBorrower ? savedContact.current?.email : null,
    savedPhone: isEditingSavedBorrower ? savedContact.current?.phone : null,
  });

  useEffect(() => {
    if (hasRegisterUrl() && formData.hasPreviousData) {
      redirectToPersonalInfo();
      return;
    }

    if (hasPersonalInfoUrl() && formData.hasPreviousData === false) {
      dealerStorage.removeItem("email-for-register");
      dealerStorage.removeItem("phone-for-register");
      redirectToRegister();
      return;
    }
  }, [formData.hasPreviousData]);

  if (storedEmail !== null && formData.confirm_email === undefined) {
    updateField("email")(storedEmail);
    updateField("confirm_email")(storedEmail);
  }

  if (storedPhone !== null && formData.phone_number === undefined) {
    updateField("phone_number")(storedPhone);
  }

  const isCoborrowerRegistration =
    hasCoborrowerUrl() ||
    userData?.roles?.coborrowers?.includes(userData?.borrower_id ?? "");

  const [
    submitHandler,
    loading,
    response,
    hasAttemptedFormSubmission,
    ,
    duplicateCoborrowerPrompt,
  ] = useDlaFormSubmit(
    async (data: RegisterFormData) => {
      if (hasCoborrowerUrl() && application_id && borrower_id) {
        return api.acceptCoborrowerInvite(
          toAcceptCoBorrowerInviteReq(data),
          application_id,
          borrower_id,
        );
      }

      if (userData?.borrower_id) {
        return api.updateBorrower(toBorrowerUpdateReq(data));
      }

      return api.startApplication(toCreateApplicationReq(data));
    },
    isCoborrowerRegistration
      ? {
          duplicateCoborrowerPrompt: (data) => {
            const firstName =
              typeof data.first_name === "string" ? data.first_name : "";
            const lastName =
              typeof data.last_name === "string" ? data.last_name : "";
            const fullName = `${firstName} ${lastName}`.trim();
            if (fullName) {
              return fullName;
            }
            if (typeof data.email === "string" && data.email) {
              return data.email;
            }
            return "This person";
          },
        }
      : undefined,
  );

  useResponseRedirect("/personal-info/citizenship", response);

  useEffect(() => {
    if (response && response.application_id && response.borrower_id) {
      dealerStorage.removeItem("email-for-register");
      dealerStorage.removeItem("phone-for-register");
    }
  }, [response]);

  const formErrors = validate<RegisterFormData>(formData, validationMapper);

  const personalInfoValue = useMemo(
    () => toPersonalInfoFormData(formData),
    [formData],
  );

  const personalInfoErrors = useMemo(
    () =>
      fieldErrorsAfterSubmit(
        {
          firstName: formErrors.first_name?.message,
          lastName: formErrors.last_name?.message,
          middleName: formErrors.middle_name?.message,
          suffix: formErrors.suffix?.message,
          email: formErrors.email?.message,
          confirmEmail: formErrors.confirm_email?.message,
          phone: formErrors.phone_number?.message,
        },
        hasAttemptedFormSubmission,
      ),
    [formErrors, hasAttemptedFormSubmission],
  );

  const smsAgreementError = fieldErrorsAfterSubmit(
    {
      sms_support_opt_out: formErrors.sms_support_opt_out?.message,
    },
    hasAttemptedFormSubmission,
  ).sms_support_opt_out;

  const onSubmit = () => {
    const formattedData = {
      ...formData,
      phone_number: addCountryCode(formData.phone_number || ""),
      dealer_id: hasCoborrowerUrl() ? undefined : dealerData.dealerId,
    };

    void submitHandler(formattedData, formErrors).then(() => {
      setUserData?.((prev) => ({
        ...prev,
        status: "IN_PROGRESS",
      }));
    });
  };

  const headerTitle = isCoborrowerRegistration
    ? "Co-Borrower"
    : "Primary Borrower";

  return (
    <FormLayout
      title="Let's get to know you better"
      headerTitle={headerTitle}
      footerTitle={hasRegisterUrl() ? "Agree & Continue" : "Next"}
      isLoading={loading}
      clickHandler={onSubmit}
    >
      <Col>
        <FormTable
          title="Personal Information"
          headerIcon={
            <UserAccountIcon
              variant="bulk-rounded"
              size={16}
              className="text-[#6F41E6]"
            />
          }
        >
          <FormTableBody>
            <PersonalInfoFields
              value={personalInfoValue}
              hideCitizenship
              showConfirmEmail={hasRegisterUrl()}
              disabled={signUpFieldLocks}
              errors={personalInfoErrors}
              onChange={(patch) => {
                const mapped = fromPersonalInfoFormData({
                  ...personalInfoValue,
                  ...patch,
                });
                for (const [key, val] of Object.entries(mapped)) {
                  if (val !== undefined) {
                    updateField(key as keyof RegisterFormData)(val);
                  }
                }
              }}
            />
          </FormTableBody>
        </FormTable>
        {hasRegisterUrl() && (
          <Disclaimer
            smsOptOut={formData.sms_support_opt_out ?? true}
            onSmsOptOutChange={(checked) =>
              updateField("sms_support_opt_out")(!checked)
            }
            errorMessage={smsAgreementError}
            hasAttemptedFormSubmission={hasAttemptedFormSubmission}
          />
        )}
      </Col>
      {duplicateCoborrowerPrompt && (
        <DuplicateCoborrowerModal
          isOpen={duplicateCoborrowerPrompt.isOpen}
          borrowerName={duplicateCoborrowerPrompt.borrowerName}
          closeHandler={duplicateCoborrowerPrompt.cancel}
          confirmHandler={duplicateCoborrowerPrompt.confirm}
          isLoading={loading}
        />
      )}
    </FormLayout>
  );
};
